// components/ChatSystem/ChatDisplay.jsx
import React, { useEffect, useRef } from 'react';
import 'katex/dist/katex.min.css';
import Latex from 'react-latex-next';

const ChatDisplay = ({ messages, isLoading = false }) => {
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const previousMessagesLength = useRef(0);
  const lastMessageRefs = useRef([]);

  // =========================================================
  // 1. CLEANUP HELPER
  // =========================================================
  // Removes: * # `  (Note: we preserve { [ ] } for LaTeX math rendering)
  const cleanDisplayString = (str) => {
    if (typeof str !== 'string') return str;
    // Regex explanation: Match *, #, or ` globally
    return str.replace(/[*#`]/g, '');
  };

  // =========================================================
  // 2. SCROLL LOGIC
  // =========================================================
  const getMessageRef = (index) => {
    if (!lastMessageRefs.current[index]) {
      lastMessageRefs.current[index] = React.createRef();
    }
    return lastMessageRefs.current[index];
  };

  const scrollToLastMessageStart = () => {
    const container = messagesContainerRef.current;
    if (!container || messages.length === 0) return;

    const lastIndex = messages.length - 1;
    const lastMessageRef = lastMessageRefs.current[lastIndex];

    if (!lastMessageRef || !lastMessageRef.current) return;

    const lastMessage = lastMessageRef.current;
    const messageTop = lastMessage.offsetTop;
    const desiredScrollTop = messageTop - (container.clientHeight * 0.15);

    container.scrollTo({
      top: Math.max(0, desiredScrollTop),
      behavior: "smooth"
    });
  };

  const scrollToShowLastMessage = () => {
    const container = messagesContainerRef.current;
    if (!container || messages.length === 0) return;

    const lastIndex = messages.length - 1;
    const lastMessageRef = lastMessageRefs.current[lastIndex];

    if (!lastMessageRef || !lastMessageRef.current) return;

    const lastMessage = lastMessageRef.current;
    const messageHeight = lastMessage.offsetHeight;
    const messageTop = lastMessage.offsetTop;
    const containerHeight = container.clientHeight;

    if (messageHeight > containerHeight * 0.7) {
      container.scrollTo({
        top: messageTop - (containerHeight * 0.1),
        behavior: "smooth"
      });
    } else {
      container.scrollTo({
        top: messageTop - (containerHeight * 0.3),
        behavior: "smooth"
      });
    }
  };

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: "smooth"
    });
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const isNewMessage = messages.length > previousMessagesLength.current;
    const hadNoMessages = previousMessagesLength.current === 0;

    if (hadNoMessages && messages.length > 0) {
      setTimeout(() => scrollToShowLastMessage(), 100);
    } else if (isNewMessage) {
      const newMessage = messages[messages.length - 1];

      if (!newMessage.isTemporary && !newMessage.isError) {
        setTimeout(() => scrollToLastMessageStart(), 50);
      } else {
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        if (isNearBottom) {
          setTimeout(() => scrollToBottom(), 50);
        }
      }
    }

    previousMessagesLength.current = messages.length;

    if (lastMessageRefs.current.length > messages.length) {
      lastMessageRefs.current = lastMessageRefs.current.slice(0, messages.length);
    }
  }, [messages]);

  // =========================================================
  // 3. TEXT RENDERING HELPERS
  // =========================================================

  // Helper: Render formatted text with bolding AND cleaning
  const renderFormattedText = (text, boldTerms = []) => {
    if (!text) return text;
    let formattedText = text;

    // Apply bold markers logic first
    if (boldTerms && boldTerms.length > 0) {
      boldTerms.forEach(term => {
        if (term && term.trim()) {
          const regex = new RegExp(`\\b(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi');
          formattedText = formattedText.replace(regex, '**$1**');
        }
      });
    }

    // Pre-process bullet points nicely
    formattedText = formattedText.replace(/^(\s*)[\-*]\s+/gm, '$1• ');

    // Split by markers, THEN clean the content parts
    return formattedText.split('**').map((part, index) => {
      const cleanedPart = cleanDisplayString(part); // CLEANUP HERE
      if (index % 2 === 1) return <strong key={index}><Latex>{cleanedPart}</Latex></strong>;
      return <Latex>{cleanedPart}</Latex>;
    });
  };

  const renderSections = (sections = []) => {
    if (!sections || sections.length === 0) return null;
    return sections.map((section, index) => (
      <div key={index} className="response-section">
        <h4 className="section-title">{cleanDisplayString(section.title)}</h4>
        <div className="section-content">{renderFormattedText(section.content)}</div>
      </div>
    ));
  };

  const renderLists = (lists = []) => {
    if (!lists || lists.length === 0) return null;
    return lists.map((list, listIndex) => (
      <div key={listIndex} className="response-list">
        {list.type === 'bullet' && (
          <ul>
            {list.items.map((item, itemIndex) => (
              <li key={itemIndex}>{renderFormattedText(item)}</li>
            ))}
          </ul>
        )}
        {list.type === 'numbered' && (
          <ol>
            {list.items.map((item, itemIndex) => (
              <li key={itemIndex}>{renderFormattedText(item)}</li>
            ))}
          </ol>
        )}
      </div>
    ));
  };

  // Helper: Old way formatting (fallback)
  const formatTextOldWay = (text) => {
    if (!text) return text;
    // Format bullet points to look distinct and correct
    let processedText = text.replace(/^(\s*)[\-*]\s+/gm, '$1• ');

    return processedText.split('**').map((part, index) => {
      const cleanedPart = cleanDisplayString(part); // CLEANUP HERE
      if (index % 2 === 1) return <strong key={index}><Latex>{cleanedPart}</Latex></strong>;
      return <Latex>{cleanedPart}</Latex>;
    });
  };

  // =========================================================
  // 4. MAIN FORMATTING LOGIC
  // =========================================================
  const formatMessageText = (message) => {
    if (!message.text) return message.text;

    // 1. System Messages
    if (message.sender === 'system') {
      return <div className="system-message-content"><Latex>{cleanDisplayString(message.text)}</Latex></div>;
    }

    // 2. Error Messages
    if (message.isError) {
      return formatTextOldWay(message.text);
    }

    // 3. User Messages
    if (message.sender === 'user') {
      return <Latex>{cleanDisplayString(message.text)}</Latex>;
    }

    // 4. Formatted Bot Responses
    if (message.sender === 'bot' && !message.isError && message.isFormatted && message.formatting) {
      return (
        <div className="formatted-response">
          {message.summary && (
            <div className="response-summary">
              <strong>📋 Summary:</strong> {cleanDisplayString(message.summary)}
            </div>
          )}

          <div className="response-content">
            {renderFormattedText(message.text, message.formatting.bold)}
          </div>
          {renderSections(message.formatting.sections)}
          {renderLists(message.formatting.lists)}
        </div>
      );
    }

    // 5. Default Fallback
    return formatTextOldWay(message.text);
  };

  return (
    <div className="chat-display" ref={messagesContainerRef}>
      {messages.length === 0 ? (
        <div className="empty-state">
          <p></p>
          <p className="empty-hint"></p>
        </div>
      ) : (
        <>
          {messages.map((message, index) => (
            <div
              key={message.id}
              ref={getMessageRef(index)}
              className={`message ${message.sender} ${message.isTemporary ? 'temporary-message' : ''}`}
              data-system-type={message.systemType}
              data-source={message.source}
              data-formatted={message.isFormatted || false}
              data-temporary={message.isTemporary || false}
            >
              <div className="message-bubble">
                <div className="message-text">
                  {formatMessageText(message)}
                </div>
                <div className="message-time">
                  {new Date(message.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                  {message.isError && <span className="error-indicator" title="Error message"> ⚠️</span>}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="message bot">
              <div className="message-bubble">
                <div className="message-text">
                  <div className="typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatDisplay;